import React, { FC, useEffect, useState } from 'react';
import Autocomplete from '@/components/elements/AutoComplete';
import { Modal } from '@/components/elements/Modal';
import AppButton from '@/components/atoms/AppButton';
import ConfigInput from '@/components/elements/ConfigInput';
import { useDispatch, useSelector } from 'react-redux';
import { PROVIDER_CONSTANT_PROPS } from '@/constant/provider/provider';
import { useToast } from '@/components/organism/Toast/ToastProvider';
import { useForm, SubmitHandler } from 'react-hook-form';
import {
  USER_FORM_EMAIL,
  USER_FORM_FIRST_NAME,
  USER_FORM_LAST_NAME,
  USER_FORM_PASSWORD,
  USER_FORM_ROLE_NAME,
  USER_PROPS,
} from '@/constant/users/user.constant';
import { createUser } from '@/services/user';
import {
  createUserFailure,
  createUserSuccess,
  getUsers,
} from '@/store/slices/user.slice';
import {
  COMMON_VALIDATIONS,
  COMMON_VALIDATION_RULES,
  EMAIL_VALIDATION_RULES,
  PASSWORD_VALIDATION_RULES,
} from '@/constant/global/global.constant';
import { IRole } from '@/services/role/role.type';
import { ICreateUserProps, IUserFormInputs } from './user.interface';
import { tid } from '@/utils/testId';

const CreateUser: FC<ICreateUserProps> = ({ isOpen, onClose, onClick }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<string[]>([]);
  const [searchRole, setSearchRole] = useState('');
  const [loading, setLoading] = useState(false);

  const rolesData = useSelector((state: any) => state?.role?.roles?.data);
  const dispatch = useDispatch();
  const toast = useToast();

  const handleSelectedRole = (value: IRole) => {
    const selectedRoleId = value?.roleId;
    setRole([...role, selectedRoleId]);
  };
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<IUserFormInputs>();

  const { onBlur: onBlurFirstName } = register(USER_FORM_FIRST_NAME);
  const { onBlur: onBlurLastName } = register(USER_FORM_LAST_NAME);
  const { onBlur: onBlurEmail } = register(USER_FORM_EMAIL);
  const { onBlur: onBlurPassword } = register(USER_FORM_PASSWORD);

  const onSubmit: SubmitHandler<IUserFormInputs> = async (formData) => {
    const { firstName, lastName, email } = formData;
    setLoading(true);
    try {
      const res = await createUser({
        email,
        password,
        firstName,
        lastName,
        roleIds: role,
      });
      dispatch(createUserSuccess((res?.data as any)?.data));
      onClose(false);
      setLoading(false);
      onClick(true);
      toast?.pushSuccess(
        (res?.data as any)?.message,
        PROVIDER_CONSTANT_PROPS.GENERIC.TOAST_TIME,
      );
    } catch (error) {
      dispatch(createUserFailure(undefined));
      setLoading(false);
      toast?.pushError(
        (error as any)?.response?.data?.message || (error as any)?.message,
        PROVIDER_CONSTANT_PROPS.GENERIC.TOAST_TIME,
      );
    }
  };

  return (
    <Modal open={isOpen} onClose={() => onClose(false)} size="lg">
      <Modal.Header title={USER_PROPS.CREATE_USER} />
      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col w-128 mb-6 px-4 pt-4 pb-2">
            <ConfigInput
              formValidationEnabled
              dataTestId={tid(`create-user-${USER_PROPS.FIRST_NAME.LABEL}`)}
              label={USER_PROPS.FIRST_NAME.LABEL}
              placeholder={USER_PROPS.FIRST_NAME.PLACEHOLDER}
              onChange={(e) => {
                onBlurFirstName(e);
                setFirstName(e.target.value);
              }}
              register={register}
              validationSchema={COMMON_VALIDATION_RULES}
              value={firstName}
              name={USER_FORM_FIRST_NAME}
              error={!!errors?.firstName}
              errorMsg={errors?.firstName && errors?.firstName?.message}
            />
            <ConfigInput
              formValidationEnabled
              dataTestId={tid(`create-user-${USER_PROPS.LAST_NAME.LABEL}`)}
              label={USER_PROPS.LAST_NAME.LABEL}
              placeholder={USER_PROPS.LAST_NAME.PLACEHOLDER}
              onChange={(e) => {
                onBlurLastName(e);
                setLastName(e.target.value);
              }}
              register={register}
              validationSchema={COMMON_VALIDATION_RULES}
              value={lastName}
              name={USER_FORM_LAST_NAME}
              error={!!errors?.lastName}
              errorMsg={errors?.lastName && errors?.lastName?.message}
            />
            <ConfigInput
              formValidationEnabled
              dataTestId={tid(`create-user-${USER_PROPS.EMAIL_ID.LABEL}`)}
              label={USER_PROPS.EMAIL_ID.LABEL}
              placeholder={USER_PROPS.EMAIL_ID.PLACEHOLDER}
              value={email}
              name={USER_FORM_EMAIL}
              type="email"
              onChange={(e) => {
                onBlurEmail(e);
                setEmail(e.target.value);
              }}
              register={register}
              validationSchema={{
                required: {
                  value: true,
                  message: COMMON_VALIDATIONS.REQUIRED_VALIDATION_MSG,
                },
                ...EMAIL_VALIDATION_RULES,
              }}
              error={!!errors?.email}
              errorMsg={errors?.email && errors?.email?.message}
            />
            <ConfigInput
              formValidationEnabled
              dataTestId={tid(`create-user-${USER_PROPS.PASSWORD.LABEL}`)}
              label={USER_PROPS.PASSWORD.LABEL}
              placeholder={USER_PROPS.PASSWORD.PLACEHOLDER}
              value={password}
              type={USER_FORM_PASSWORD}
              name={USER_FORM_PASSWORD}
              onChange={(e) => {
                onBlurPassword(e);
                setPassword(e.target.value);
              }}
              register={register}
              validationSchema={{
                ...PASSWORD_VALIDATION_RULES,
                required: {
                  value: true,
                  message: COMMON_VALIDATIONS.REQUIRED_VALIDATION_MSG,
                },
              }}
              error={!!errors?.password}
              errorMsg={errors?.password && errors?.password?.message}
            />
            <Autocomplete
              nameKey={USER_FORM_ROLE_NAME}
              dataTestId={tid(`create-user-${USER_PROPS.ROLE.PLACEHOLDER}`)}
              placeholder={USER_PROPS.ROLE.PLACEHOLDER}
              label={USER_PROPS.ROLE.LABEL}
              handleSelectedValue={handleSelectedRole as any}
              handleSearch={setSearchRole}
              lists={rolesData}
              multiSelect
            />
          </div>
          <div className="grid grid-cols-1 gap-4 px-2 py-2">
            <AppButton
              tabIndex={3}
              data-testid={tid('user-create-save-btn')}
              type="submit"
              variant="primary"
              fullWidth
              loading={isSubmitting || loading}
              disabled={isSubmitting || loading}
            >
              {isSubmitting || loading
                ? USER_PROPS.BUTTON.WHILE_LOADING_IN_CREATION
                : USER_PROPS.BUTTON.SAVE}
            </AppButton>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default CreateUser;
